import React, { useEffect, useCallback } from 'react';
import queryString from 'query-string';
import { useHistory } from 'react-router-dom';

import AuthContext from '../AuthContext';
import {
  SHOW_WELCOME_URL_PARAM,
  SUBMITTER_USER_ID_PARAM,
} from '../../Router/onboarding/constants';

const QUERY_PARAM = SHOW_WELCOME_URL_PARAM;

export type OnboardingWelcomeModalContextType = {
  visible: boolean;
  openModal: () => void;
  closeModal: () => void;
  queryParam: typeof QUERY_PARAM;
  submitterQueryParam: typeof SUBMITTER_USER_ID_PARAM;
  /** The flow is to show the welcome modal, then depending on the
   * role/stage of onboarding display the wizard or the prelaunch modal
   * Upon closing these modals the global banner should display.  */
  isDoneWelcomeFlow: boolean;
  setIsDoneWelcomeFlow: (done: boolean) => void;
};

const OnboardingWelcomeModalContext =
  React.createContext<OnboardingWelcomeModalContextType>(
    {} as OnboardingWelcomeModalContextType,
  );

export const OnboardingWelcomeModalContextProvider: React.FC = ({
  children,
}) => {
  const [visible, setVisible] = React.useState(false);
  const [isDoneWelcomeFlow, setIsDoneWelcomeFlow] = React.useState(true);

  const { auth, logout, upgradePlanDataLoaded, isUnclaimedOnAgentPlan } =
    React.useContext(AuthContext);

  const history = useHistory();
  const query = queryString.parse(history.location.search);

  const openModal: OnboardingWelcomeModalContextType['openModal'] =
    useCallback(() => {
      if (isUnclaimedOnAgentPlan) return;
      setVisible(true);
      setIsDoneWelcomeFlow(false);
    }, [setVisible, setIsDoneWelcomeFlow, isUnclaimedOnAgentPlan]);

  const closeModal: OnboardingWelcomeModalContextType['closeModal'] =
    useCallback(() => {
      const queryParams = new URLSearchParams(history.location.search);
      queryParams.delete(QUERY_PARAM);
      queryParams.delete(SUBMITTER_USER_ID_PARAM);
      if (queryParams.get('sso')) {
        queryParams.delete('sso');
      }
      history.replace({ search: queryParams.toString() });

      setVisible(false);
    }, [history, setVisible]);

  useEffect(() => {
    const state = query?.[QUERY_PARAM]?.toString();
    const submitterId = query?.[SUBMITTER_USER_ID_PARAM]?.toString();
    if (submitterId && submitterId !== auth!.id) {
      // This is for Org onboarding, claimed submitter case. If the user is logged into the wrong account, log them out
      logout();
      return;
    }

    if (state === 'true') {
      if (!upgradePlanDataLoaded) return;
      openModal();
    }
  }, [query, openModal, logout, auth!.id, upgradePlanDataLoaded]);

  return (
    <OnboardingWelcomeModalContext.Provider
      value={{
        visible,
        openModal,
        closeModal,
        queryParam: QUERY_PARAM,
        isDoneWelcomeFlow,
        setIsDoneWelcomeFlow,
        submitterQueryParam: SUBMITTER_USER_ID_PARAM,
      }}
    >
      {children}
    </OnboardingWelcomeModalContext.Provider>
  );
};

export default OnboardingWelcomeModalContext;
